// =============================================================================
// supplier-me-client.ts — Client API du RÔLE DE SESSION fournisseur (S0-ROLE, Option B).
// =============================================================================
// Miroir HTTP des deux lectures de session fournisseur consommées par le role-gating de l'UI :
//   - GET /supplier/auth/whoami        → { siret }  (BC identity, session opaque)
//   - GET /supplier/contacts/me        → { contactId, role, nom, prenom, email, state }  (BC supplier-reference)
// Option B (GATE_HUMAN) : le RÔLE fournisseur (MASTER / COLLABORATEUR) est EXPOSÉ par supplier-reference
// et consommé ici pour dériver `AuthState.supplierRole`. La présence d'une session fournisseur (whoami OK)
// ⇒ `AuthState.role = 'supplier'`.
//
// LECTURE PURE : aucune mutation, aucune règle métier ici (autorité = API : siret ET contactId viennent
// de la SESSION, jamais du body — invariant n°1). Le siret n'est PAS dans le contrat `me` : il vient de
// whoami. AUCUNE donnée métier en localStorage (RULE_REF_006) ; la session est portée par le cookie
// httpOnly `supplier_session` (credentials:'include' côté HTTP).
//
// DTO = miroirs HTTP ASCII (parité SupplierMeView côté API). La SPA ne couple PAS au paquet DB.

/** Rôle du contact fournisseur au sein du SIRET (miroir HTTP de ContactRole). */
export type SupplierMeRoleDto = 'MASTER' | 'COLLABORATEUR';

/** État du contact fournisseur (miroir HTTP de ContactState). Renfort TEXTE, jamais une couleur seule. */
export type SupplierMeStateDto = 'Invite' | 'Actif' | 'Inactif' | 'ResignatureRequise';

/** Réponse de `GET /supplier/auth/whoami` : le siret résolu par l'interceptor sur la session. */
export interface SupplierWhoamiDto {
  readonly siret: string;
}

/**
 * Réponse de `GET /supplier/contacts/me` (self-lookup, PAS de restriction MASTER-only). Le siret n'y
 * figure PAS (il vient de whoami) : ce contrat porte l'identité du contact ET son rôle.
 */
export interface SupplierMeDto {
  readonly contactId: string;
  readonly role: SupplierMeRoleDto;
  /** Nom de famille (PII). */
  readonly nom: string;
  /** Prénom (PII). */
  readonly prenom: string;
  /** Email vérifié du contact (PII) — coordonnées À CONFIRMER au 1er accès (F3, DBU:2359). */
  readonly email: string;
  readonly state: SupplierMeStateDto;
  /**
   * P-6 — RAISON SOCIALE de l'entreprise, pour que l'espace fournisseur la nomme au lieu d'afficher son
   * SIRET. `null` quand aucune source ne la porte (fournisseur sans relevé d'enrichissement) : l'écran
   * retombe alors sur le SIRET, jamais sur un blanc.
   */
  readonly raisonSociale: string | null;
}

/** Genre d'erreur applicative de la résolution de session fournisseur. */
export type SupplierMeApiErrorKind = 'unauthorized' | 'generic';

/**
 * Erreur applicative renvoyée par les lectures de session (HTTP 4xx/5xx). Le `kind` 'unauthorized'
 * (401) signale l'ABSENCE de session fournisseur : le consommateur (bootstrap auth) retombe alors sur
 * l'interim admin. L'UI ne réinterprète JAMAIS la règle métier (autorité = API).
 */
export class SupplierMeApiError extends Error {
  public readonly kind: SupplierMeApiErrorKind;

  public constructor(message: string, kind: SupplierMeApiErrorKind = 'generic') {
    super(message);
    this.name = 'SupplierMeApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client « rôle de session fournisseur » — miroir des deux lectures de session (whoami + me).
 * LECTURE PURE : le siret ET le contactId viennent de la session (jamais du body). Le bootstrap auth
 * orchestre les deux appels ; ce client reste fidèle à chaque endpoint (une méthode = un endpoint).
 */
export interface SupplierMeClient {
  /** GET /supplier/auth/whoami — { siret } de la session fournisseur ; lève 'unauthorized' si pas de session. */
  whoami(): Promise<SupplierWhoamiDto>;
  /** GET /supplier/contacts/me — self-lookup { contactId, role, ... } ; lève 'unauthorized' si pas de session. */
  getMe(): Promise<SupplierMeDto>;
}
